import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import ErrorMessage from '../../ErrorMessage';
import { useLoginContext } from '../LoginContext';
import { LockIcon } from '../../../assets/icons';

const INPUT_IDS = ['mfa-login-0', 'mfa-login-1', 'mfa-login-2', 'mfa-login-3', 'mfa-login-4', 'mfa-login-5'];

function Step3({ authenticateSubmit }) {
  const { userCode, setUserCode, error } = useLoginContext();
  const [code, setCode] = useState(() => (typeof userCode === 'string' && userCode.length === 6 ? userCode.split('') : ['', '', '', '', '', '']));
  const inputRefs = useRef([]);
  const formRef = useRef(null);

  useEffect(() => {
    if (typeof userCode === 'string' && userCode.length === 6 && /^\d{6}$/.test(userCode)) {
      setCode(userCode.split(''));
    }
  }, [userCode]);

  useEffect(() => {
    setUserCode(code.join(''));
  }, [code, setUserCode]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleCodeChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pastedData)) {
      setCode(pastedData.split(''));
      inputRefs.current[5]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (code.join('').length === 6) {
        formRef.current?.requestSubmit();
      }
      return;
    }
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const codeString = code.join('');
    setUserCode(codeString);
    setTimeout(() => authenticateSubmit(e), 0);
  };

  return (
    <div className='w-full overflow-hidden rounded-[20px] bg-white px-6 py-9 shadow-[0_4px_32px_rgba(11,25,41,0.08)] sm:px-12 sm:py-11'>
      <div className='text-center'>
        <div className='mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#EBF4FB] text-[#0F2540]'>
          <LockIcon className='h-7 w-7' strokeWidth='1.75' />
        </div>
        <h5 className='font-serif text-2xl font-normal leading-tight text-[#0B1929]'>Enter verification code.</h5>
        <p className='mt-2 text-sm leading-6 text-[#7A9AB0]'>Please enter your one-time password.</p>
      </div>
      <form ref={formRef} onSubmit={handleSubmit} className='mt-7 space-y-4'>
        <div className='space-y-2'>
          <div className='flex justify-center'>
            <div className='flex items-center gap-2'>
              {code.map((digit, index) => (
                <input
                  key={INPUT_IDS[index]}
                  id={INPUT_IDS[index]}
                  ref={(el) => {
                    if (el) inputRefs.current[index] = el;
                  }}
                  type='text'
                  inputMode='numeric'
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  className='flex h-12 w-11 items-center justify-center rounded-[10px] border-[1.5px] border-[#DDE6EF] bg-white text-center text-lg font-semibold text-[#0B1929] outline-none transition focus:border-[#2E6BAD] focus:ring-4 focus:ring-[#2E6BAD]/10 disabled:cursor-not-allowed disabled:opacity-50'
                />
              ))}
            </div>
          </div>
        </div>
        <button
          type='submit'
          disabled={code.join('').length !== 6}
          className='inline-flex h-12 w-full items-center justify-center rounded-full bg-[#0B1929] px-7 text-sm font-semibold text-white transition hover:bg-[#0F2540] disabled:cursor-not-allowed disabled:bg-[#B8C9D9]'
        >
          Submit
        </button>
        {error && <ErrorMessage error={error} className='form-error-display' />}
      </form>
    </div>
  );
}

Step3.propTypes = {
  authenticateSubmit: PropTypes.func.isRequired,
};

export default Step3;
